import { State, Action, StateContext, Selector, Store } from '@ngxs/store'
import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import { Observable, of, throwError } from 'rxjs'

import { RtaService } from '../../services/rta.service'
import { BitFeature, BOM_FixedCutterInput, BomPartType, CoGs, FixedCutterPreBOMInput, Order_Base } from '../../interfaces/request'
import { RequestActions } from '../action/request.action'
import { CutterMap } from '../../interfaces/cutterMap'
import { IORD_Order, ITaskViewModel, ORD_Bit, OrderFeatureAssociation, PatchORDArmDto, PatchORDBitDto, PatchORDCerebroDto, PatchORDCutterMapDto, PatchOrderTaskDto, PatchORDGaugeProtectionDto, PatchORDOrderDto, PatchORDPowderDto, PatchORDUppersectionDto, PatchRollerConeDto, ReturnViewModel, Task, TaskViewModel } from '../../interfaces/request.interface'
import { RTAApiClient } from '../../services/rta-api.service'
import { GlobalNotificationService } from '../../services/globalnotification.service'
import { discardPeriodicTasks } from '@angular/core/testing'
import { CombinedSelectors } from './combined.selectors'

export interface RequestStateModel {
  requests: IORD_Order[]
  request?: IORD_Order,
  taskSet?: ITaskViewModel[]
  baseRequest: Order_Base,
  cogs: CoGs,
  cutterMapping: CutterMap,
  fixedCutterSapData: BOM_FixedCutterInput,
  picklist: any[],
  isLoadingSAPData: boolean,
  feature: BitFeature[],
  bomLaytout: BomPartType[],
  drafts: {
    [key: string]: Partial<PatchORDOrderDto> & {
      bit?: PatchORDBitDto;
      cerebro?: PatchORDCerebroDto;
      cutterMaps?: PatchORDCutterMapDto[];
      orderTasks?: PatchOrderTaskDto[];
      uppersection?: PatchORDUppersectionDto;
      gaugeProtections?: PatchORDGaugeProtectionDto[];
      powders?: PatchORDPowderDto[];
      bitFeatureIds?: number[];
      arm?: PatchORDArmDto;
      rollerCone?: PatchRollerConeDto;
    }
  };
}

@State<RequestStateModel>({
  name: 'request',
  defaults: {
    requests: [],
    request: undefined,
    taskSet: [],
    baseRequest: {} as Order_Base,
    cogs: {} as CoGs,
    cutterMapping: {} as CutterMap,
    fixedCutterSapData: {} as BOM_FixedCutterInput,
    picklist: [],
    isLoadingSAPData: false,
    feature: [],
    bomLaytout: [],
    drafts: {}
  },
})
@Injectable()
export class RequestState {
  constructor(private service: RtaService, private rtaAPIService: RTAApiClient, private globalNotificationService: GlobalNotificationService, private store: Store) { }

  @Selector()
  static getAll(state: RequestStateModel): IORD_Order[] {
    return state.requests
  }

  @Selector()
  static getById(state: RequestStateModel): (id: number) => IORD_Order | undefined {
    return (id: number) => state.requests.find(r => r.id === id)
  }
  @Selector()
  static getRequest(state: RequestStateModel): IORD_Order | undefined {
    return state.request;
  }
  @Selector()
  static sizeByDraft(state: RequestStateModel): (draftId: number) => number | undefined {
    return (draftId: number) => state.drafts[draftId]?.bit?.size;
  }
  @Selector()
  static draftByIdFn(state: RequestStateModel): (draftId: number) => Partial<PatchORDOrderDto> & { bit?: PatchORDBitDto; bitFeatureIds?: number[] } {
    return draftId => state.drafts[draftId] ?? {};
  }
  @Selector()
  static productLine(state: RequestStateModel): number {
    return state.request?.productLine ?? 0;
  }
  @Selector()
  static orderTasks(state: RequestStateModel): ITaskViewModel[] | undefined {
    return state?.taskSet;
  }
  @Selector()
  static fixedCutterPreInput(state: RequestStateModel): BOM_FixedCutterInput {
    return state?.fixedCutterSapData;
  }


  @Selector([
    RequestState.getRequest,
    RequestState.getCoGs
  ])
  static getReviewData(
    getRequest: IORD_Order | undefined,
    cogs: CoGs | null
  ) {
    return (id: number) => ({
      getRequest,
      cogs
    });
  }

  @Action(RequestActions.CreateRequest)
  createRequest(ctx: StateContext<RequestStateModel>, action: RequestActions.CreateRequest): Observable<ReturnViewModel> {
    return this.rtaAPIService.upsertOrder(0, action.payload.userId, action.payload.request).pipe(
      switchMap(vm => {
        if (vm.status && vm.info) {
          const newOrder = vm.info;
          const state = ctx.getState();
          ctx.patchState({
            requests: [...state.requests, newOrder],
            request: newOrder
          });
          this.globalNotificationService.showSuccessNotification(`Request ${newOrder.id} created successfully`);
          return of(vm);
        }
        else {
          this.globalNotificationService.showErrorNotification(`Failed to create request: ${vm.message}`);
          return throwError(() => new Error(vm.message ?? 'Failed to create request'));
        }
      })
    )
  }
  
  @Action(RequestActions.LoadTasks)
  getOrderTasks(ctx: StateContext<RequestStateModel>, action: RequestActions.LoadTasks): Observable<ITaskViewModel[]> {
    return this.rtaAPIService.getOrderTasks(action.orderId).pipe(
      tap(tasks => {
        if (tasks) {
          ctx.patchState({ taskSet: [...tasks] });
        } else {
          this.globalNotificationService.showErrorNotification(`Failed to load tasks`);
        }
      })
    );
  }
  @Action(RequestActions.GetRequestDetails)
  getRequestDetails(
    ctx: StateContext<RequestStateModel>,
    action: RequestActions.GetRequestDetails
  ): Observable<ReturnViewModel> {
    return this.rtaAPIService.rTA(action.payload.id).pipe(
      tap(vm => {
        const fetched = vm.info!;

        fetched.requiredDate = fetched.requiredDate
          ? new Date(fetched.requiredDate as any)
          : null;

        const state = ctx.getState();
        const key = fetched.id.toString();
        const bitFeatureIds = (fetched.orderFeatureAssociations ?? [])
          .map((a: OrderFeatureAssociation) => a.bitFeatureID)
          .filter((id: number | undefined): id is number => typeof id === 'number');

        // 1) keep your requests array up to date
        const exists = state.requests.some(r => r.id === fetched.id);
        const requests = state.requests.some(r => r.id === fetched.id)
  ? state.requests.map(r => r.id === fetched.id ? fetched : r)
  : [...state.requests, fetched];

        ctx.patchState({
          requests,
          request: fetched,
          drafts: {
            ...state.drafts,
            [key]: {
              requiredDate: fetched.requiredDate,
              productLine: fetched.productLine,
              applicationId: fetched.applicationId,
              activeProblems: fetched.activeProblems,
              referenceBitSummaryId: fetched.referenceBitSummaryId,
              fieldRequestId: fetched.fieldRequestId,
              nwA_ApplicationId: fetched.nwA_ApplicationId,
              typeId: fetched.typeId,
              priority: fetched.priority,
              toolSize: fetched.toolSize,
              notesToDesignSupport: fetched.notesToDesignSupport,
              description: fetched.description,
              difference: fetched.difference,
              regionId: fetched.regionId,
              requestExclusion: fetched.requestExclusion,
              targetCustomer: fetched.targetCustomer,
              bit: {
                ...(fetched.bit as PatchORDBitDto),
                bitFeatureIds
              } as PatchORDBitDto,
              uppersection: fetched.uppersection as PatchORDUppersectionDto[],
              powders: fetched.powders as PatchORDPowderDto[],
              bitFeatureIds
            }
          }
        });
      })
    );
  }
  @Action(RequestActions.UpdateRequest)
  updateRequest(ctx: StateContext<RequestStateModel>, action: RequestActions.UpdateRequest): Observable<ReturnViewModel> {
    return this.rtaAPIService.upsertOrder(action.payload.key, action.payload.userId, action.payload.request).pipe(
      switchMap(vm => {
        if (vm.status && vm.info) {
          const updated = vm.info;
          ctx.patchState({ request: updated })
          this.globalNotificationService.showSuccessNotification(`Request ${updated.id} update successfully`);
          return of(vm);
        }
        else {
          this.globalNotificationService.showErrorNotification(`Failed to update request: ${vm.message}`);
          return throwError(() => new Error(vm.message ?? 'Failed to update request'));
        }
      })
    )
  }

  @Action(RequestActions.DeleteRequest)
  deleteRequest(ctx: StateContext<RequestStateModel>, action: RequestActions.DeleteRequest): Observable<void> {
    return this.service.deleteRequest(action.payload.id).pipe(
      tap(() => {
        const state = ctx.getState()
        ctx.patchState({ requests: state.requests.filter(r => r.id !== action.payload.id) })
      })
    )
  }

  @Action(RequestActions.ListRequest)
  listRequest(ctx: StateContext<RequestStateModel>): Observable<IORD_Order[]> {
    return this.service.getRequests().pipe(
      tap(data => ctx.patchState({ requests: data }))
    )
  }
  @Action(RequestActions.PatchRequest)
  patchRequest(
    ctx: StateContext<RequestStateModel>,
    { request }: RequestActions.PatchRequest
  ) {
    const existing = ctx.getState().request || {} as IORD_Order;
    ctx.patchState({
      request: { ...existing, ...request }
    });
  }
  @Action(RequestActions.PatchRequestDraft)
  patchDraft(ctx: StateContext<RequestStateModel>, { payload }: RequestActions.PatchRequestDraft) {
    const state = ctx.getState();
    const key = payload.draftId != null ? payload.draftId.toString() : 'new';
    const existing = state.drafts[key] || {};
    ctx.patchState({
      drafts: {
        ...state.drafts,
        [key]: { ...existing, ...payload.changes }
      }
    });
  }

  @Action(RequestActions.SaveDraft)
  saveDraft(ctx: StateContext<RequestStateModel>, { payload }: RequestActions.SaveDraft) {
    const state = ctx.getState();
    const key = payload.draftId != null ? payload.draftId.toString() : 'new';
    const draft = state.drafts[key]!;
    const orderId = payload.draftId || 0;
    const isNew = payload.draftId == null;

    // Resolve current visibility context
    const productLine = this.store.selectSnapshot(CombinedSelectors.activeProductLine) ?? '';
    const subPSL = this.store.selectSnapshot(CombinedSelectors.activeSubPSL) ?? '';
    const isFixedCutter = subPSL === 'Fixed Cutter';
    const isMatrix = productLine === 'Matrix';
    const isDHT = subPSL === 'DHT';
    const isRollerCone = subPSL === 'Roller Cone';

    const ops = [
      isNew
        ? new RequestActions.CreateRequest({ request: draft, userId: payload.userId })
        : new RequestActions.UpdateRequest({ request: draft, userId: payload.userId, key: payload.draftId! })
    ];
    if (key !== 'new') {
      // Bit data — visible for Fixed Cutter
      const featureIds = draft.bitFeatureIds ?? [];
      const hasBitPayload = !!draft.bit || featureIds.length > 0;

      if (hasBitPayload && isFixedCutter) {
        const bitPayload = new PatchORDBitDto({
          ...(draft.bit ?? {}),
          bitFeatureIds: featureIds,
          orderId: orderId || (draft.bit?.orderId ?? undefined)
        });
        ctx.dispatch(new RequestActions.UpsertOrderBit({
          bit: bitPayload,
          userId: payload.userId,
          key: orderId
        }));
      }

      if (draft.cerebro) {
        ctx.dispatch(new RequestActions.UpsertOrderCerebro({
          cerebro: draft.cerebro,
          userId: payload.userId,
          key: orderId
        }));
      }

      // Gauge protections — visible for Fixed Cutter only
      if (draft.gaugeProtections && isFixedCutter) {
        ctx.dispatch(new RequestActions.UpdateOrderGauge({
          gauge: draft.gaugeProtections,
          userId: payload.userId,
          key: orderId
        }));
      }

      // Powders — visible for Matrix product line only
      if (draft.powders && isMatrix) {
        ctx.dispatch(new RequestActions.UpdateOrderPowderBinder({
          powder: draft.powders,
          userId: payload.userId,
          key: orderId
        }))
      }

      // Cutter maps — visible for Fixed Cutter and DHT
      if (draft.cutterMaps && (isFixedCutter || isDHT)) {
        ctx.dispatch(new RequestActions.UpdateOrderCutterMap({
          cutterMap: draft.cutterMaps,
          userId: payload.userId,
          key: orderId
        }))
      }

      // Arm — visible for DHT only
      if (draft.arm && isDHT) {
        ctx.dispatch(new RequestActions.UpsertOrderArm({
          arm: draft.arm,
          userId: payload.userId,
          key: orderId
        }));
      }

      // Roller cone — visible for Roller Cone only
      if (draft.rollerCone && isRollerCone) {
        ctx.dispatch(new RequestActions.UpsertRollerCone({
          rollerCone: draft.rollerCone,
          userId: payload.userId,
          key: orderId
        }));
      }
    }


    return ctx.dispatch(ops);
  }
  @Action(RequestActions.UpsertOrderBit)
  upsertOrderBit(ctx: StateContext<RequestStateModel>, { payload }: RequestActions.UpsertOrderBit) {
    return this.rtaAPIService.upsertOrderBit(payload.key, payload.userId, payload.bit).pipe(
      tap(vm => {
        if (vm.status) {
          this.globalNotificationService.showSuccessNotification(`Bit saved`);
        }
      })
    );
  }
  @Action(RequestActions.UpsertOrderArm)
  upsertOrderArm(ctx: StateContext<RequestStateModel>, { payload }: RequestActions.UpsertOrderArm) {
    return this.rtaAPIService.upsertOrderArm(payload.key, payload.userId, payload.arm).pipe(
      tap(vm => {
        if (vm.status) {
          this.globalNotificationService.showSuccessNotification(`Arm data saved`);
        }
      })
    );
  }
  @Action(RequestActions.UpsertRollerCone)
  upsertRollerCone(ctx: StateContext<RequestStateModel>, { payload }: RequestActions.UpsertRollerCone) {
    return this.rtaAPIService.upsertRollerCone(payload.key, payload.userId, payload.rollerCone).pipe(
      tap(vm => {
        if (vm.status) {
          this.globalNotificationService.showSuccessNotification(`Roller cone data saved`);
        }
      })
    );
  }
  @Action(RequestActions.PostCommunication)
  postCommunication(ctx: StateContext<RequestStateModel>, { payload }: RequestActions.PostCommunication) {
    return this.rtaAPIService.postOrderCommunication(payload.key, payload.communication).pipe(
      tap(vm => {
        if (!vm.status) {
          this.globalNotificationService.showErrorNotification(vm.message ?? 'There was an error saving your communication');
        }
      })
    );
  }
  @Action(RequestActions.UpsertOrderCerebro)
  upsertOrderCerebro(ctx: StateContext<RequestStateModel>, { payload }: RequestActions.UpsertOrderCerebro) {
    return this.rtaAPIService.upsertOrderCerebro(payload.key, payload.userId, payload.cerebro).pipe(
      tap(vm => {
        if (vm.status) {
          this.globalNotificationService.showSuccessNotification(`Cerebro data saved`);
        }
      })
    );
  }

  // @Action(RequestActions.UpdateOrderCutterMap)
  // updateOrderCutterMap(ctx: StateContext<RequestStateModel>, { payload }: RequestActions.UpdateOrderCutterMap) {
  //   return this.rtaAPIService.updateOrderCutterMap(payload.key, payload.userId, payload.cutterMap).pipe(
  //     tap(vm => {
  //       if (vm.status) {
  //         this.globalNotificationService.showSuccessNotification(`Cutter map row ${payload.cutterMap.id} saved`);
  //       }
  //     })
  //   );
  // }

  @Action(RequestActions.UpdateOrderTask)
  updateOrderTask(ctx: StateContext<RequestStateModel>, { payload }: RequestActions.UpdateOrderTask) {
    return this.rtaAPIService.updateOrderTask(payload.key, payload.task).pipe(
      tap(vm => {
        if (vm.status) {
          this.globalNotificationService.showSuccessNotification(`Task ${payload.task.id} saved`);
        }
      })
    );
  }

  @Action(RequestActions.UpdateOrderGauge)
  updateOrderGauge(ctx: StateContext<RequestStateModel>, { payload }: RequestActions.UpdateOrderGauge) {
    return this.rtaAPIService
      .updateOrderGaugeProtection(payload.key, payload.userId, payload.gauge)
      .pipe(
        tap(vm => {
          if (vm.status) {
            this.globalNotificationService.showSuccessNotification(
              `Gauge protections saved`
            );
          } else {
            this.globalNotificationService.showErrorNotification(
              `Failed to save gauge protections: ${vm.message}`
            );
          }
        })
      );
  }
  @Action(RequestActions.UpdateOrderPowderBinder)
  updateOrderPowderBinder(ctx: StateContext<RequestStateModel>, { payload }: RequestActions.UpdateOrderPowderBinder) {
    return this.rtaAPIService.updateOrderPowder(payload.key, payload.userId, payload.powder).pipe(
      tap(vm => {
        if (vm.status) {
          this.globalNotificationService.showSuccessNotification(`Powder/Binder saved`);
        }
      })
    );
  }
  @Action(RequestActions.UpdateOrderCutterMap)
  updateOrderCutterMap(
    ctx: StateContext<RequestStateModel>,
    { payload }: RequestActions.UpdateOrderCutterMap
  ) {
    return this.rtaAPIService
      .updateOrderCutterMap(payload.key, payload.userId, payload.cutterMap)
      .pipe(
        tap(vm => {
          if (vm.status) {
            this.globalNotificationService.showSuccessNotification(
              `Cutter map saved`
            );
          } else {
            this.globalNotificationService.showErrorNotification(
              `Failed to save cutter map: ${vm.message}`
            );
          }
        })
      );
  }

  @Action(RequestActions.LoadCoGs)
  loadCoGs(ctx: StateContext<RequestStateModel>, action: RequestActions.LoadCoGs) {
    return this.rtaAPIService.getEstimatedCostSavings(action.orderId).pipe(
      map(resp => {
        const c: CoGs = {
          RtaNumber: action.orderId,
          ReferenceMaterialNumber: resp.reference?.leachingDepth ?? '',
          Ref_PrimaryCutter: resp.reference?.primaryCutterCost?.toString() ?? '',
          Ref_BackupElement: resp.reference?.backupElementCost?.toString() ?? '',
          Ref_OverallLeach: resp.reference?.leachingDepth ?? '',
          Ref_Powder: resp.reference?.powder?.averageCost?.toString() ?? '',
          Ref_Binder: resp.reference?.binder?.averageCost?.toString() ?? '',
          Ref_Hardfacing: resp.reference?.hardFacing?.averageCost?.toString() ?? '',
          Ref_Uppersection: resp.reference?.upperSection?.averageCost?.toString() ?? '',
          Ref_OnePiece: resp.reference?.isOnePiece ?? false,
          Rta_PrimaryCutter: resp.rta?.primaryCutterCost?.toString() ?? '',
          Rta_BackupElement: resp.rta?.backupElementCost?.toString() ?? '',
          Rta_OverallLeach: resp.rta?.leachingDepth ?? '',
          Rta_Powder: resp.rta?.powder?.averageCost?.toString() ?? '',
          Rta_Binder: resp.rta?.binder?.averageCost?.toString() ?? '',
          Rta_Hardfacing: resp.rta?.hardFacing?.averageCost?.toString() ?? '',
          Rta_Uppersection: resp.rta?.upperSection?.averageCost?.toString() ?? '',
          Rta_OnePiece: resp.rta?.isOnePiece ?? false,
          Delta_Matrix2Steel: resp.reference?.matrixToSteel?.toString() ?? '',
          Total_Cost: resp.rta?.totalCost?.toString() ?? '',
          Total_Savings: resp?.totalSavings?.toString() ?? '',
          HideReference: resp.reference?.isOnePiece ?? false,
          Justification: resp.technicalJustification ?? ''
        };
        ctx.dispatch(new RequestActions.SetCoGs(c));
      })
    );
  }

  @Action(RequestActions.SetCoGs)
  setCoGs(ctx: StateContext<RequestStateModel>, action: RequestActions.SetCoGs) {
    ctx.patchState({ cogs: action.cogs });
  }

  @Selector()
  static getCoGs(state: RequestStateModel): CoGs | null {
    return state.cogs;
  }

  @Action(RequestActions.StartTask)
startTask(ctx: StateContext<RequestStateModel>, action: RequestActions.StartTask) {
  return this.rtaAPIService.startOrderTask(action.key).pipe(
    tap(vm => {
      if (vm.status) {
        this.globalNotificationService.showSuccessNotification(`Task started successfully`);

        if (action.orderId !== undefined) {
          ctx.dispatch(new RequestActions.LoadTasks(action.orderId));
        }
      }
    })
  );
}
  @Action(RequestActions.CompleteTask)
  completeTask(ctx: StateContext<RequestStateModel>, action: RequestActions.CompleteTask) {
    if(action?.notes)
      action.notes = ' ';
    return this.rtaAPIService.completeOrderTask(action.key, action.notes).pipe(
      tap(vm => {
        if (vm.status) {
          this.globalNotificationService.showSuccessNotification(`Task completed successfully`);
          
          if (action.orderId !== undefined) {
            ctx.dispatch(new RequestActions.LoadTasks(action.orderId));
          }
        }
      })
    );
  }
  @Action(RequestActions.RollbackOrderTask)
  rollbackOrderTask(ctx: StateContext<RequestStateModel>, action : RequestActions.RollbackOrderTask) {
    return this.rtaAPIService.rollbackOrderTask(action.key).pipe(
      tap(vm => {
        if (vm.status) {
          if (action.orderId !== undefined) {
            ctx.dispatch(new RequestActions.LoadTasks(action.orderId));
          }
          this.globalNotificationService.showSuccessNotification(`Rollback successful`);
        }
      })
    );
  }
  @Action(RequestActions.RollbackSubOrderTask)
  rollbackSubOrderTask(ctx: StateContext<RequestStateModel>, action: RequestActions.RollbackSubOrderTask) {
    return this.rtaAPIService.rollbackSubOrderTask(action.key).pipe(
      tap(vm => {
        if (vm.status) {
          if (action.orderId !== undefined) {
            ctx.dispatch(new RequestActions.LoadTasks(action.orderId));
          }
          this.globalNotificationService.showSuccessNotification(`Rollback successful`);
        }
      })
    );
  }
  @Action(RequestActions.ApproveOrderTask)
  approveOrderTask(ctx: StateContext<RequestStateModel>, action: RequestActions.ApproveOrderTask) {
    return this.rtaAPIService.approveOrderTask(action.key).pipe(
      tap(vm => {
        if (vm.status) {
          this.globalNotificationService.showSuccessNotification('Task approved successfully');
          if (action.orderId !== undefined) {
            ctx.dispatch(new RequestActions.LoadTasks(action.orderId));
            ctx.dispatch(new RequestActions.GetRequestDetails({ id: action.orderId.toString() }));
          }
        } else {
          this.globalNotificationService.showErrorNotification(vm.message ?? 'Failed to approve task');
        }
      }),
      catchError(err => {
        this.globalNotificationService.showErrorNotification(`Failed to approve task: ${err.message}`);
        return of(null);
      })
    );
  }
  @Action(RequestActions.RejectOrderTask)
  rejectOrderTask(ctx: StateContext<RequestStateModel>, action: RequestActions.RejectOrderTask) {
    return this.rtaAPIService.rejectOrderTask(action.key, action.reason).pipe(
      tap(vm => {
        if (vm.status) {
          this.globalNotificationService.showSuccessNotification('Task rejected. The request has been kicked back for revision.');
          if (action.orderId !== undefined) {
            ctx.dispatch(new RequestActions.LoadTasks(action.orderId));
            ctx.dispatch(new RequestActions.GetRequestDetails({ id: action.orderId.toString() }));
          }
        } else {
          this.globalNotificationService.showErrorNotification(vm.message ?? 'Failed to reject task');
        }
      }),
      catchError(err => {
        this.globalNotificationService.showErrorNotification(`Failed to reject task: ${err.message}`);
        return of(null);
      })
    );
  }
  @Action(RequestActions.ProcessRework)
  processRework(ctx: StateContext<RequestStateModel>, action: RequestActions.ProcessRework) {
    return this.rtaAPIService.processRework(action.key, action.reworkProcesses).pipe(
      tap(vm => {
        if (vm.status) {
          this.globalNotificationService.showSuccessNotification(`Selected rework processes have been processed successfully`);
        }
      })
    );
  }
  @Action(RequestActions.ProcessOnHold)
  processOnHold(ctx: StateContext<RequestStateModel>, action: RequestActions.ProcessOnHold) {
    return this.rtaAPIService.processOnHold(action.key, action.patchonholdDto?.reason||'', action.patchonholdDto.onHoldCat_Id, action.patchonholdDto.order_Id).pipe(
      tap(vm => {
        if (vm.status) {
          this.globalNotificationService.showSuccessNotification(`On hold successfully set`);
          ctx.dispatch(new RequestActions.GetRequestDetails({id: action.patchonholdDto.order_Id.toString()}));
          ctx.dispatch(new RequestActions.LoadTasks(action.patchonholdDto.order_Id));
        }
      })
    );
  }
  @Action(RequestActions.RemoveHold)
  removeHold(ctx: StateContext<RequestStateModel>, action: RequestActions.RemoveHold) {
    return this.rtaAPIService.removeOnHold(action.key).pipe(
      tap(vm => {
        if (vm.status) {
          this.globalNotificationService.showSuccessNotification(`Selected rework processes have been processed successfully`);
          ctx.dispatch(new RequestActions.GetRequestDetails({id: action.key.toString()}));
          ctx.dispatch(new RequestActions.LoadTasks(action.key));
        }
      })
    );
  }
  @Action(RequestActions.AddNewTask)
  addNewTask(ctx: StateContext<RequestStateModel>, action: RequestActions.AddNewTask) {
    return this.rtaAPIService.addNewTask(action.taskTemplate, action.orderId, action.startQueue).pipe(
      tap(vm => {
        if (vm.status) {
          this.globalNotificationService.showSuccessNotification('NC task added successfully');
          ctx.dispatch(new RequestActions.LoadTasks(action.orderId));
        } else {
          this.globalNotificationService.showErrorNotification(vm.info?.toString() ?? 'Failed to add NC task');
        }
      })
    );
  }
  @Action(RequestActions.DeleteOrderTask)
  deleteOrderTask(ctx: StateContext<RequestStateModel>, action: RequestActions.DeleteOrderTask) {
    return this.rtaAPIService.deleteOrderTask(action.taskId, action.orderId).pipe(
      tap(vm => {
        if (vm.status) {
          this.globalNotificationService.showSuccessNotification('Task deleted successfully');
          ctx.dispatch(new RequestActions.LoadTasks(action.orderId));
        } else {
          this.globalNotificationService.showErrorNotification(vm.info?.toString() ?? 'Failed to delete task');
        }
      })
    );
  }
  @Action(RequestActions.DeleteOrderProcess)
  deleteOrderProcess(ctx: StateContext<RequestStateModel>, action: RequestActions.DeleteOrderProcess) {
    return this.rtaAPIService.deleteOrderProcess(action.taskId, action.orderId).pipe(
      tap(vm => {
        if (vm.status) {
          this.globalNotificationService.showSuccessNotification('Process and all related tasks deleted successfully');
          ctx.dispatch(new RequestActions.LoadTasks(action.orderId));
        } else {
          this.globalNotificationService.showErrorNotification(vm.info?.toString() ?? 'Failed to delete process');
        }
      })
    );
  }
  @Action(RequestActions.GetFixedCutterPreBOM)
  getFixedCutterPreBOM(ctx: StateContext<RequestStateModel>, action: RequestActions.GetFixedCutterPreBOM): Observable<BOM_FixedCutterInput> {
    ctx.patchState({ isLoadingSAPData: true });
    return this.rtaAPIService.getPopulateSAPData(action.payload.id).pipe(
      map(sap => {
        if (sap.status && sap.info) {
          return sap.info as BOM_FixedCutterInput;
        }
        throw new Error('Invalid SAP data response');
      }),
      tap(fixedCutterSapData => {
        ctx.patchState({
          fixedCutterSapData,
          isLoadingSAPData: false
        });
      }),
      catchError(err => {
        ctx.patchState({ isLoadingSAPData: false });
        this.globalNotificationService.showErrorNotification(`Failed to load SAP data: ${err.message}`);
        return of({} as BOM_FixedCutterInput);
      })
    );
  }

  @Action(RequestActions.SubmitRequest)
  submitRequest(ctx: StateContext<RequestStateModel>, { payload }: RequestActions.SubmitRequest) {
    return this.service.submitOrder(payload.orderId).pipe(
      tap(vm => {
        if (vm.status) {
          this.globalNotificationService.showSuccessNotification(`Order ${payload.orderId} submitted successfully`);
          ctx.dispatch(new RequestActions.SubmitRequestSuccess({ orderId: payload.orderId }));
          // Refresh the request details to get updated status
          ctx.dispatch(new RequestActions.GetRequestDetails({ id: payload.orderId.toString() }));
        } else {
          this.globalNotificationService.showErrorNotification(vm.message ?? 'Failed to submit order');
          ctx.dispatch(new RequestActions.SubmitRequestFailure({ orderId: payload.orderId, error: vm.message ?? 'Unknown error' }));
        }
      }),
      catchError(err => {
        this.globalNotificationService.showErrorNotification(`Failed to submit order: ${err.message}`);
        ctx.dispatch(new RequestActions.SubmitRequestFailure({ orderId: payload.orderId, error: err.message }));
        return of(null);
      })
    );
  }
  
}
